import { Injectable } from "@angular/core";
import { AccountAppCommand } from "@fabric-types/app-account";
import { FabricDesignSystemLibCommand } from "@fabric-types/lib-fabric-design-system";
import { ConfigServiceValue, Jurisdiction } from "@fabric-types/svc-config";
import { ContentGroupType, ContentValue } from "@fabric-types/svc-content";
import { Action } from "@ngrx/store";
import { AlertButtonStyle } from "@obg/mat.ui.core";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { NETLOSS_LIMIT_80_ID } from "@obg/ngx.common";
import {
	CORE_ROUTE,
	HGC_DEPOSIT_LIMIT_80_ID,
	DEPOSIT_LIMIT_100_ID,
	NET_DEPOSIT_LIMIT_100_ID,
	KycActionsAction,
	KycRealTimeChange,
	MessageType,
	SessionAction,
} from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { minutesToMilliseconds } from "date-fns";
import { Observable, take, tap } from "rxjs";

import { CustomerInteractionsAction } from "../customer-interactions/customer-interactions.action";
import { CustomerLimitNotificationsAction } from "../customer-limit-notifications/customer-limit-notifications.actions";
import { NotificationResultRedirectAction } from "../rg-interactions/notification-result/notification-result.model";
import { RgInteractionsAction } from "../rg-interactions/rg-interactions.action";
import {
	RG_INTERACTIONS_ALERT_DEPOSIT_ID,
	RG_INTERACTIONS_ALERT_LOSS_ID,
	RG_INTERACTIONS_ALERT_TOOLS_ID,
	RG_INTERACTIONS_ALERT_WAGERING_ID,
} from "../rg-interactions/rg-interactions.const";
import { RgInteractionType } from "../rg-interactions/rg-interactions.model";

import { RealTimeNotificationsConfig } from "./real-time-notifications.config";
import {
	BalanceLimitExceededRealTimeMessagePayload,
	BettingLimitReachedRealTimeMessagePayload,
	CustomerInteractionRealTimeMessagePayload,
	DepositLimitReachedRealTimeMessagePayload,
	RealTimeChannel,
	RTKycChangePayload,
} from "./real-time-notifications.model";

@Injectable({
	providedIn: "root",
})
export class RealTimeNotificationsService {
	constructor(
		private config: RealTimeNotificationsConfig,
		private sessionAction: SessionAction,
		private kycAction: KycActionsAction,
		private documentRef: DocumentRef
	) {}

	processStreamMessages(message: string): Action | ActionWithPayload | undefined {
		const parsedMessage = this.tryParse(message) as RealTimeChannel;
		if (!parsedMessage) {
			return this.processStreamStringPayload(message);
		}

		if (parsedMessage.ActionType) {
			return this.kycAction.customerActionsChange();
		}

		const parsedPayload =
			typeof parsedMessage.Payload === "string" ? this.tryParse(parsedMessage.Payload) : parsedMessage.Payload;

		return parsedPayload
			? this.processStreamObjectPayload(parsedMessage.Topic, parsedPayload)
			: this.processStreamStringPayload(parsedMessage.Payload);
	}

	private processStreamStringPayload(message: string): Action | ActionWithPayload | undefined {
		const REMAINING_SESSION_TIME_5_MINUTES = 5;
		const REMAINING_SESSION_TIME_10_MINUTES = 10;
		const REMAINING_SESSION_TIME_15_MINUTES = 15;
		if (message.includes(this.config.getSessionLimit80NotificationMessage())) {
			const session80Regex = new RegExp(this.config.getSessionLimit80NotificationMessage(), "g");
			const sessionType = message.replace(session80Regex, "");
			return CustomerLimitNotificationsAction.openSnackBar({
				message: `session-limit-notification.80.${sessionType}`,
				type: MessageType.Info,
				doNotShowAgainButton: false,
				keepOpen: true,
			});
		}
		if (message.includes(this.config.getSessionLimit100NotificationMessage())) {
			new Observable(this.documentRef.obgEventBus.retrieve(ConfigServiceValue.Jurisdiction))
				.pipe(
					take(1),
					tap((jurisdiction) => {
						if (jurisdiction === Jurisdiction.Aams) {
							this.displayDialogBox(
								"session-limit-notification.title",
								"Session Limit",
								CORE_ROUTE.responsibleGaming.sessionLimit
							);
							return;
						}
						const session100Regex = new RegExp(this.config.getSessionLimit100NotificationMessage(), "g");
						const sessionType = message.replace(session100Regex, "");
						CustomerLimitNotificationsAction.openSnackBar({
							message: `session-limit-notification.100.${sessionType}`,
							type: MessageType.Error,
							doNotShowAgainButton: false,
							keepOpen: true,
						});
					})
				) // eslint-disable-next-line rxjs/no-ignored-subscription
				.subscribe();
			return undefined;
		}
		if (message === this.config.getSession2MinutesSystemNotificationMessage()) {
			return CustomerLimitNotificationsAction.openSnackBar({
				message: "hgc.session-limit-notification.system.2",
				type: MessageType.Error,
				doNotShowAgainButton: false,
				keepOpen: true,
				dismissibleDuration: 8000,
			});
		}
		if (message === this.config.getSession10MinutesSystemNotificationMessage()) {
			return CustomerLimitNotificationsAction.openSnackBar({
				message: "hgc.session-limit-notification.system.10",
				type: MessageType.Info,
				doNotShowAgainButton: false,
				keepOpen: true,
				dismissibleDuration: 8000,
			});
		}
		if (message === this.config.getSessionExpiryNotificationMessage()) {
			return batchActions(
				RouteAction.navigate(CORE_ROUTE.home),
				this.sessionAction.logout({ shouldSkipNotification: true }),
				CustomerLimitNotificationsAction.openSessionExpiryAlert({
					limitPrefix: "hgc.session-limit-notification.expiry",
					redirectButtonAction: RouteAction.navigate(CORE_ROUTE.auth.login),
					prefixJurisdiction: true,
				})
			);
		}
		if (message === this.config.getDepositLimit80NotificationMessage()) {
			return CustomerLimitNotificationsAction.openSnackBar({
				message: "hgc.deposit-limit-notification.80",
				type: MessageType.Info,
				doNotShowAgainButton: false,
				id: HGC_DEPOSIT_LIMIT_80_ID,
				keepOpen: true,
			});
		}
		if (message === this.config.getDepositLimit100NotificationMessage()) {
			new Observable(this.documentRef.obgEventBus.retrieve(ConfigServiceValue.Jurisdiction))
				.pipe(
					take(1),
					tap((jurisdiction) => {
						if (jurisdiction === Jurisdiction.Aams) {
							this.displayDialogBox(
								"deposit-limit.title",
								"Deposit Limit",
								CORE_ROUTE.responsibleGaming.depositLimit
							);
							return;
						}
						CustomerLimitNotificationsAction.openSnackBar({
							message: "deposit-limit.notification.100",
							type: MessageType.Info,
							doNotShowAgainButton: false,
							id: DEPOSIT_LIMIT_100_ID,
							keepOpen: true,
						});
					})
				) // eslint-disable-next-line rxjs/no-ignored-subscription
				.subscribe();
			return undefined;
		}
		if (message === this.config.getNetDepositLimit100NotificationMessage()) {
			new Observable(this.documentRef.obgEventBus.retrieve(ConfigServiceValue.Jurisdiction))
				.pipe(
					take(1),
					tap((jurisdiction) => {
						if (jurisdiction === Jurisdiction.Aams) {
							this.displayDialogBox(
								"net-deposit-limit.title",
								"Net Deposit Limit",
								CORE_ROUTE.responsibleGaming.netDepositLimit
							);
							return;
						}
						CustomerLimitNotificationsAction.openSnackBar({
							message: "net-deposit-limit.notification.100",
							type: MessageType.Info,
							doNotShowAgainButton: false,
							id: NET_DEPOSIT_LIMIT_100_ID,
							keepOpen: true,
						});
					})
				)
				// eslint-disable-next-line rxjs/no-ignored-subscription
				.subscribe();
			return undefined;
		}
		if (message === this.config.getNetLossLimit80NotificationMessage()) {
			return CustomerLimitNotificationsAction.openSnackBar({
				message: "netloss-limit.notification.80",
				type: MessageType.Info,
				doNotShowAgainButton: false,
				id: NETLOSS_LIMIT_80_ID,
				prefixJurisdiction: true,
				duration: 15000,
			});
		}
		if (message === this.config.getNetLossLimit100NotificationMessage()) {
			new Observable(this.documentRef.obgEventBus.retrieve(ConfigServiceValue.Jurisdiction))
				.pipe(
					take(1),
					tap((jurisdiction) => {
						if (jurisdiction === Jurisdiction.Aams) {
							this.displayDialogBox(
								"netloss-limit.title",
								"Net Loss Limit",
								CORE_ROUTE.responsibleGaming.netlossLimit
							);
							return;
						}
						CustomerLimitNotificationsAction.openAlert({
							limitPrefix: "netloss-limit.notification.100",
							redirectButtonAction: RouteAction.navigate(CORE_ROUTE.responsibleGaming.netlossLimit),
							prefixJurisdiction: true,
							buttonStyle: AlertButtonStyle.Flat,
						});
					})
				) // eslint-disable-next-line rxjs/no-ignored-subscription
				.subscribe();
			return undefined;
		}
		if (message === this.config.getWageringLimit80NotificationMessage()) {
			return CustomerLimitNotificationsAction.openSnackBar({
				message: "betting-limit.notification.80",
				type: MessageType.Info,
				doNotShowAgainButton: false,
				prefixJurisdiction: true,
				keepOpen: true,
			});
		}
		if (message === this.config.getWageringLimit100NotificationMessage()) {
			return CustomerLimitNotificationsAction.openAlert({
				limitPrefix: "betting-limit.notification.100",
				redirectButtonAction: RouteAction.navigate(CORE_ROUTE.responsibleGaming.bettingLimit),
				prefixJurisdiction: true,
				buttonStyle: AlertButtonStyle.Flat,
			});
		}
		if (message === this.config.getLossTriggerMessage()) {
			return RgInteractionsAction.verifyInteraction({
				type: RgInteractionType.Loss,
				id: RG_INTERACTIONS_ALERT_LOSS_ID,
				redirectButtonText: "rg-interactions.more-information",
				redirectButtonAction: RouteAction.navigate(CORE_ROUTE.responsibleGaming.timeOff),
				notificationResultAction: NotificationResultRedirectAction.TimeoutPage,
			});
		}
		if (message === this.config.getWageringTriggerMessage()) {
			return RgInteractionsAction.verifyInteraction({
				type: RgInteractionType.Wagering,
				id: RG_INTERACTIONS_ALERT_WAGERING_ID,
				redirectButtonText: "rg-interactions.more-information",
				redirectButtonAction: RouteAction.navigate(CORE_ROUTE.responsibleGaming.timeOff),
				notificationResultAction: NotificationResultRedirectAction.TimeoutPage,
			});
		}
		if (message === this.config.getDepositTriggerMessage()) {
			return RgInteractionsAction.verifyInteraction({
				type: RgInteractionType.Deposit,
				id: RG_INTERACTIONS_ALERT_DEPOSIT_ID,
				redirectButtonText: "deposit-limit.title",
				redirectButtonAction: RouteAction.navigate(CORE_ROUTE.responsibleGaming.depositLimit),
				notificationResultAction: NotificationResultRedirectAction.DepositLimitPage,
			});
		}
		if (message === this.config.getToolsTriggerMessage()) {
			return RgInteractionsAction.verifyInteraction({
				type: RgInteractionType.Tools,
				id: RG_INTERACTIONS_ALERT_TOOLS_ID,
				redirectButtonText: "ukgc.rg-interactions.more-information",
				redirectButtonAction: RouteAction.navigate(CORE_ROUTE.responsibleGaming.selfExclusion),
				notificationResultAction: NotificationResultRedirectAction.SelfExclusionPage,
			});
		}
		if (message.includes(this.config.getSessionLimit5NotificationMessage())) {
			return CustomerLimitNotificationsAction.handleSessionLimitNotification({
				remainingSessionTimeInMillis: minutesToMilliseconds(REMAINING_SESSION_TIME_5_MINUTES),
			});
		}
		if (message.includes(this.config.getSessionLimit10NotificationMessage())) {
			return CustomerLimitNotificationsAction.handleSessionLimitNotification({
				remainingSessionTimeInMillis: minutesToMilliseconds(REMAINING_SESSION_TIME_10_MINUTES),
			});
		}
		if (message.includes(this.config.getSessionLimit15NotificationMessage())) {
			return CustomerLimitNotificationsAction.handleSessionLimitNotification({
				remainingSessionTimeInMillis: minutesToMilliseconds(REMAINING_SESSION_TIME_15_MINUTES),
			});
		}
		if (message.includes(this.config.getSelfExclusionNotificationMessage())) {
			return CustomerLimitNotificationsAction.openSelfExclusionAlert();
		}

		return undefined;
	}

	private processStreamObjectPayload(topic: string, payload: object): Action | ActionWithPayload | undefined {
		if (topic === this.config.getKycTriggerTopic()) {
			const { KycStatus, KycStatusDetails } = payload as RTKycChangePayload;
			const kycRTChange: KycRealTimeChange = {
				kycStatus: KycStatus,
				kycStatusDetails: KycStatusDetails,
			} as KycRealTimeChange;
			return this.kycAction.kycStatusChange(kycRTChange);
		}

		if (topic === this.config.getBetLimitNotificationTopic()) {
			const { MessageId, LimitType, LimitPeriod, ProductCategories, TimeStamp } =
				payload as BettingLimitReachedRealTimeMessagePayload;
			const actionPayload: BettingLimitReachedRealTimeMessagePayload = {
				MessageId,
				LimitType,
				LimitPeriod,
				ProductCategories,
				TimeStamp,
			};

			return CustomerLimitNotificationsAction.openBettingLimitReachedAlert(actionPayload);
		}

		if (topic === this.config.getBalanceLimitExceededTopic()) {
			const { Amount, MessageId } = payload as BalanceLimitExceededRealTimeMessagePayload;
			const actionPayload: BalanceLimitExceededRealTimeMessagePayload = {
				MessageId,
				Amount,
			};
			return CustomerLimitNotificationsAction.openBalanceLimitExceededAlert(actionPayload);
		}

		if (topic === this.config.getDepositLimitReachedTopic()) {
			const { MessageId, LimitType, Limit } = payload as DepositLimitReachedRealTimeMessagePayload;
			const actionPayload: DepositLimitReachedRealTimeMessagePayload = {
				MessageId,
				LimitType,
				Limit,
			};
			return CustomerLimitNotificationsAction.openDepositLimitReachedAlert(actionPayload);
		}

		if (topic === this.config.getSessionLimitReachedTopic()) {
			return CustomerLimitNotificationsAction.openSessionLimitReachedAlert();
		}

		if (
			topic === this.config.getCustomerInteractionsTimeTriggerTopic() ||
			topic === this.config.getCustomerInteractionsToolsTriggerTopic() ||
			topic === this.config.getCustomerInteractionsWageringTriggerTopic() ||
			topic === this.config.getCustomerInteractionsLossTriggerTopic() ||
			topic === this.config.getCustomerInteractionsDepositTriggerTopic()
		) {
			const customerInteractionMessage = payload as CustomerInteractionRealTimeMessagePayload;
			return CustomerInteractionsAction.openAlert(customerInteractionMessage);
		}

		if (topic === this.config.getCustomerMadeDepositTopic() && this.documentRef.obgEventBus) {
			this.documentRef.obgEventBus?.dispatch(AccountAppCommand.RefetchThresholdStatus);
			return undefined;
		}

		return undefined;
	}

	private tryParse(payload: string): object | undefined {
		try {
			return JSON.parse(payload);
		} catch {
			return undefined;
		}
	}

	private displayDialogBox(
		titleKey: string,
		alertType: "Session Limit" | "Deposit Limit" | "Net Deposit Limit" | "Net Loss Limit",
		adjustmentRoute: string
	) {
		new Observable(
			this.documentRef.obgEventBus.retrieve(ContentValue.getTranslations, {
				key: titleKey,
				requiredGroup: [ContentGroupType.Common, ContentGroupType.General],
			})
		)
			.pipe(
				take(1),
				tap((translation: Record<string, string>) => {
					this.documentRef.obgEventBus.dispatch(FabricDesignSystemLibCommand.DialogShow, {
						header: {
							title: translation[titleKey] ? translation[titleKey] : titleKey,
							showCloseButton: true,
							showBackButton: false,
						},

						bodyElement: "responsible-gaming_limit-reached-alert",
						bodyElementProps: {
							alertType: alertType,
							adjustmentRoute: adjustmentRoute,
						},

						footerElement: "account-modal_footer",

						template: "custom",
						position: "default",
						size: "medium",
						context: "surface-variant",
						cancelable: false,
					});
				})
			) // eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}
}
